import { wxCall } from "./wx-call";
export interface DurableStorage {
  read(key: string): Promise<string | null>;
  write(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
  clear(): Promise<void>;
}
export function browserStorage(name: string): DurableStorage {
  let connection: Promise<IDBDatabase> | undefined;
  const db = () =>
    (connection ??= new Promise((resolve, reject) => {
      const req = indexedDB.open(name, 1);
      req.onupgradeneeded = () => req.result.createObjectStore("values");
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => {
        connection = undefined;
        reject(new Error("无法打开战斗存档"));
      };
    }));
  async function access<T>(
    mode: IDBTransactionMode,
    operation: (store: IDBObjectStore) => IDBRequest,
  ): Promise<T> {
    const database = await db();
    return new Promise((resolve, reject) => {
      const tx = database.transaction("values", mode),
        req = operation(tx.objectStore("values"));
      tx.oncomplete = () => resolve(req.result as T);
      tx.onerror = () => reject(new Error("存档写入失败，请检查设备空间"));
      tx.onabort = () => reject(new Error("存档操作中断"));
    });
  }
  return {
    read: async (key) =>
      (await access<string | undefined>("readonly", (s) => s.get(key))) ?? null,
    write: async (key, value) => {
      await access("readwrite", (s) => s.put(value, key));
    },
    remove: async (key) => {
      await access("readwrite", (s) => s.delete(key));
    },
    clear: async () => {
      await access("readwrite", (s) => s.clear());
    },
  };
}
export function wechatStorage(wx: any): DurableStorage {
  let fs: any;
  let generation = 0;
  const indexKey = (key: string) => "friend-draw:flight-file:" + key;
  const root = () => wx.env.USER_DATA_PATH + "/doodle-flight";
  const init = () => {
    if (!fs) {
      fs = wx.getFileSystemManager();
      try {
        fs.mkdirSync(root(), true);
      } catch {}
    }
  };
  const path = (key: string) =>
    root() + "/" + encodeURIComponent(key) + ".json";
  const call = (method: string, args: object) => {
    init();
    return wxCall<any>(
      ({ success, fail }) => fs[method]({ ...args, success, fail }),
      { onTimeout: () => new Error("战斗存档操作超时") },
    );
  };
  return {
    async read(key) {
      try {
        return String(
          (
            await call("readFile", {
              filePath: wx.getStorageSync(indexKey(key)) || path(key),
              encoding: "utf8",
            })
          ).data,
        );
      } catch (e) {
        if (/no such|not exist|ENOENT/i.test(String((e as any)?.errMsg ?? e)))
          return null;
        throw e;
      }
    },
    async write(key, value) {
      const previous = wx.getStorageSync(indexKey(key)) || path(key);
      const final =
          path(key) +
          "." +
          Date.now().toString(36) +
          "-" +
          (++generation).toString(36),
        temporary = final + ".tmp";
      await call("writeFile", {
        filePath: temporary,
        data: value,
        encoding: "utf8",
      });
      await call("rename", { oldPath: temporary, newPath: final });
      try {
        wx.setStorageSync(indexKey(key), final);
      } catch (error) {
        void call("unlink", { filePath: final }).catch(() => {});
        throw error;
      }
      void call("unlink", { filePath: previous }).catch(() => {});
    },
    async remove(key) {
      const filePath = wx.getStorageSync(indexKey(key)) || path(key);
      wx.removeStorageSync(indexKey(key));
      try {
        await call("unlink", { filePath });
      } catch (e) {
        if (!/no such|not exist|ENOENT/i.test(String((e as any)?.errMsg ?? e)))
          throw e;
      }
    },
    async clear() {
      const result = await call("readdir", { dirPath: root() });
      for (const name of result.files)
        await call("unlink", { filePath: root() + "/" + name });
      for (const key of wx.getStorageInfoSync().keys)
        if (key.startsWith("friend-draw:flight-file:"))
          wx.removeStorageSync(key);
    },
  };
}
